import { useContext } from 'react';
import { useLocation } from 'react-router-dom';
import useCoders from 'hooks/useCoders';
import CodePageModalContext from 'projects/components/contexts/CodePageModalContext';

// Single source of truth for the X-Delve-Page header value.
//
// Callers pass the result + their own `surface` to coding mutations:
//
//   const page = useTrackingPage();
//   mutation.mutate({ ..., trackingContext: { page, surface: 'code_tag' } });
//
// Resolution order:
//   1. If the CodePageModal is open (state-based — used when the modal
//      pops up on top of a transcript/snippets/chat page rather than
//      navigating to /codes/:id), return code_detail. We consult the
//      existing modal-open state directly, not a tracking-specific
//      context: the modal already needs that state to know whether to
//      render at all, so it's the honest source of "is the user
//      currently looking at code detail?".
//   2. The /transcripts/:id/user/me coder filter → coded_by_me.
//   3. URL pathname → page.
//
// Known limitation: AiCodingModal also opens on top of /transcripts/:id
// without a URL change AND doesn't have a state-based context we can
// consult here, so coding inside it lands as page=transcript. Pinned by
// a Cypress test in tracking_headers_spec so it doesn't drift.
export default function useTrackingPage() {
  // All hooks must run on every render in stable order.
  const codePageModal = useContext(CodePageModalContext);
  const location = useLocation();
  const pathname = location?.pathname || '';
  const coders = useCoders(pathname);

  if (codePageModal?.isOpen) return 'code_detail';
  if (coders.length > 0) return 'coded_by_me';

  if (pathname.includes('/chat')) return 'ai_chat';
  if (pathname.includes('/analysis')) return 'snippets';
  if (pathname.includes('/survey_questions')) return 'survey_question';
  if (pathname.includes('/survey_responses')) return 'survey_response';
  if (pathname.includes('/compare')) return 'coding_comparison';
  if (pathname.includes('/reports')) return 'reports';
  // A specific code's detail view (the code-detail modal is also caught above
  // via codePageModal.isOpen).
  if (pathname.match(/\/projects\/[^/]+\/codes\/[^/]+/)) return 'code_detail';
  // The codes tab itself (codebook list in the left-hand menu) — a distinct
  // surface from the code-detail modal, so it gets its own page value rather
  // than falling through to the 'transcript' default.
  if (pathname.match(/\/projects\/[^/]+\/codes(\/|$)/)) return 'codes';
  return 'transcript';
}
